import { useContext } from 'react';
import { Link, useInRouterContext, useLocation } from 'react-router-dom';
import { useTranslation } from '../../../i18n/useTranslation';
import { ActiveAquariumContext } from '../../../contexts/AquariumContext';
import { isZeovitSupplySystem } from '../../../utils/supplySystem';
import { navigationConfig } from '../../../config/navigation';
import { aquariumIdFromPath, findActiveArea, tabHref, tabMatches, visibleTabs } from '../../../config/navigationMatch';
import './SectionTabs.css';

/**
 * The pages of the current area as a row of tabs above the page (usability audit
 * §4.1): "Wasser" shows Werte & Verlauf · ICP · Zielwerte · Integrationen. Links,
 * not ARIA tabs — each tab is its own page with its own URL; the current one has
 * aria-current="page". Nothing renders outside an area or with a single tab.
 */
export function SectionTabs() {
  return useInRouterContext() ? <RoutedSectionTabs /> : null;
}

function RoutedSectionTabs() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const active = useContext(ActiveAquariumContext);
  const area = findActiveArea(navigationConfig, pathname);
  if (area === null) return null;

  const aquariumId = aquariumIdFromPath(pathname) ?? active?.activeAquariumId ?? null;
  const tabs = visibleTabs(area.item, {
    aquariumId,
    usesZeovit: isZeovitSupplySystem(active?.activeAquarium?.supplySystem),
  });
  if (tabs.length < 2) return null;

  const areaLabel = t(area.item.labelKey);
  return (
    <nav className="rg-section-tabs" aria-label={t('nav.sectionTabs', { section: areaLabel })} data-testid="section-tabs">
      <ul className="rg-section-tabs__list">
        {tabs.map((tab) => {
          const current = tabMatches(tab, pathname);
          return (
            <li key={tab.labelKey} className="rg-section-tabs__item">
              <Link
                to={tabHref(tab, aquariumId)!}
                className={current ? 'rg-section-tabs__link rg-section-tabs__link--current' : 'rg-section-tabs__link'}
                aria-current={current ? 'page' : undefined}
              >
                {t(tab.labelKey)}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
